import { useLayoutEffect, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useEntityFeatures } from '@/hooks/use-entity-features';
import { EntityFeaturesContext } from '@/features/tenancy/entity-features-context';

/** Shared booking screens keep their member content while visitor panels unmount. */
export function VisitorFeatureBoundary({ children, fallback = null }: { children: ReactNode; fallback?: ReactNode }) {
  const features = useEntityFeatures();
  const client = useQueryClient();
  // Unscoped directory guests are trimmed by each record's owning entity on the server.
  const admitted = !features.tenantSlug || (!features.isPending && !features.isError &&
    !features.isFetching && !features.isPlaceholderData && features.isEnabled('visitors'));
  useLayoutEffect(() => {
    if (admitted) return;
    const predicate = (query: { queryKey: readonly unknown[] }) => query.queryKey.some(key =>
      typeof key === 'string' && /^\/api\/(visits(?:\/|$)|admin\/visits|visitor-(self|booking))/.test(key));
    void client.cancelQueries({ predicate }).then(() => client.resetQueries({ predicate }));
  }, [admitted, client]);
  return <EntityFeaturesContext.Provider value={features}>{admitted ? children : fallback}</EntityFeaturesContext.Provider>;
}
